import React, { useMemo } from 'react'
import { TrackProps } from 'react-player'

import { ProgressBar } from '../../atoms/ProgressBar'
import { Dropdown, Option } from '../../molecules/Dropdown'
import { IconButton } from '../../molecules/IconButton'
import { mq, styled } from '../../../styled'

type Time = {
  label: string
  value: number
}

export type Props = {
  currentQuality: Option
  currentTime: Time
  duration: Time
  isFullscreen: boolean
  isMuted: boolean
  isPlaying: boolean
  isShowingTracks?: boolean
  isUserIdle: boolean
  onSubtitleClick: React.MouseEventHandler<HTMLButtonElement>
  onFullscreenChange: React.MouseEventHandler<HTMLButtonElement>
  onMuteClick: React.MouseEventHandler<HTMLButtonElement>
  onPlayClick: React.MouseEventHandler<HTMLButtonElement>
  onPlaybackRateChange: (value: Option) => void
  onQualityChange: (value: Option) => void
  onSeek: React.ChangeEventHandler<HTMLInputElement>
  onVolumeChange: React.ChangeEventHandler<HTMLInputElement>
  playbackRate: Option
  playbackRateOption: Option[]
  qualityOption: Option[]
  tracks?: TrackProps[]
  volume: number
}

const ControllerWrapper = styled.div<Pick<Props, 'isPlaying' | 'isUserIdle'>>`
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: ${({ theme }) => theme.zIndexes.video.ctrl};
  display: flex;
  align-items: center;
  background-color: rgba(0, 0, 0, 0.2);
  opacity: ${({ isPlaying, isUserIdle }) =>
    isPlaying && isUserIdle ? `0` : `1`};
  visibility: ${({ isPlaying, isUserIdle }) =>
    isPlaying && isUserIdle ? 'hidden' : 'visible'};
  transition: opacity 0.2s, visibility 0.2s;
`

const ControllerButton = styled(IconButton)`
  border-radius: 0;
  color: ${({ theme }) => theme.colors.white};

  &:hover {
    color: ${({ theme }) => theme.colors.blue};
  }
`

const CaptionButton = styled(IconButton)<Pick<Props, 'isShowingTracks'>>`
  border-radius: 0;
  color: ${({ theme, isShowingTracks }) =>
    isShowingTracks ? theme.colors.blue : theme.colors.white};

  &:hover {
    color: ${({ theme, isShowingTracks }) =>
      isShowingTracks ? theme.colors.white : theme.colors.blue};
  }
`

const Time = styled.span`
  display: none;
  color: ${({ theme }) => theme.colors.white};
  font-size: ${({ theme }) => theme.sizes.font.s14};
  margin: 0 1em;

  ${mq.md} {
    display: block;
  }
`

const Volume = styled.div`
  display: none;

  ${mq.md} {
    display: flex;
    align-items: center;
  }
`

const SeekBar = styled(ProgressBar)`
  flex-grow: 2;
`

const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent)

export const Controller: React.FC<Props> = ({
  currentQuality,
  currentTime,
  duration,
  isFullscreen,
  isMuted,
  isPlaying,
  isShowingTracks,
  isUserIdle,
  onSubtitleClick,
  onFullscreenChange,
  onMuteClick,
  onPlayClick,
  onPlaybackRateChange,
  onQualityChange,
  onSeek,
  onVolumeChange,
  playbackRate,
  playbackRateOption,
  qualityOption,
  tracks,
  volume,
}) => {
  const hasTracks = useMemo<boolean>(
    () => tracks !== undefined && tracks.length !== 0,
    [tracks]
  )

  return (
    <ControllerWrapper isPlaying={isPlaying} isUserIdle={isUserIdle}>
      <ControllerButton
        bgColor="transparent"
        icon={isPlaying ? 'pause' : 'play'}
        onClick={onPlayClick}
      />
      {!isIOS && (
        <Volume>
          <ControllerButton
            bgColor="transparent"
            icon={isMuted ? 'volume-off' : 'volume-up'}
            onClick={onMuteClick}
          />
          <ProgressBar
            max={1.0}
            onChange={onVolumeChange}
            step={0.1}
            value={isMuted ? 0 : volume}
            width={48}
          />
        </Volume>
      )}
      <Time>{currentTime.label}</Time>
      <SeekBar
        max={duration.value}
        onChange={onSeek}
        value={currentTime.value}
        width={70}
      />
      <Time>{duration.label}</Time>
      <Dropdown
        label={playbackRate.label}
        onChange={onPlaybackRateChange}
        options={playbackRateOption}
        value={playbackRate.value}
      />

      <Dropdown
        label={currentQuality.label}
        onChange={onQualityChange}
        options={qualityOption}
        value={currentQuality.value}
      />

      {hasTracks && (
        <CaptionButton
          bgColor="transparent"
          icon="closed-captioning"
          isShowingTracks={isShowingTracks}
          onClick={onSubtitleClick}
        />
      )}

      <ControllerButton
        bgColor="transparent"
        icon={isFullscreen ? 'compress' : 'expand'}
        onClick={onFullscreenChange}
      />
    </ControllerWrapper>
  )
}
